import { Lst } from "@/shared/lib/lst/lst";
import { Filter } from "@/shared/lib/filter/filter";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";

/**
 * MasterDataPdfAddressData holds the lines of the address window.
 *
 * SenderLines are printed above the recipient when the company
 * sends the document, ReceiverLines when the company is the
 * recipient.
 */
export interface MasterDataPdfAddressData {
  senderLines: string[];
  receiverLines: string[];
}

/**
 * MasterDataPdfHeaderData holds the two images of the letterhead.
 * Both are SVG data URIs, see {@link isSvgDataUri}.
 */
export interface MasterDataPdfHeaderData {
  claim: string;
  logo: string;
}

/**
 * MasterDataPdfFooterData holds the logo strip of the footer: the
 * logos of the group with the issuing company's own highlighted.
 */
export interface MasterDataPdfFooterData {
  logo1: string;
  logo2: string;
  logo3: string;
  logo4: string;
}

/**
 * MasterDataPdfInfoblockLine is one line of the info block.
 *
 * With a key set, the key is printed left and the value right.
 * Without one, the value is printed on its own.
 */
export interface MasterDataPdfInfoblockLine {
  key: string;
  value: string;
  bold: boolean;
}

/** A group of lines. Groups are separated by a small gap. */
export interface MasterDataPdfInfoblockGroup {
  lines: MasterDataPdfInfoblockLine[];
}

export interface MasterDataPdfInfoblockData {
  groups: MasterDataPdfInfoblockGroup[];
}

/**
 * MasterDataPdfDefault is the letterhead master data of a company
 * for the one PDF template style in use.
 *
 * A company may own several, told apart by their label.
 */
export interface MasterDataPdfDefault {
  id: string;
  label: string;
  companyId: string;
  addressData: MasterDataPdfAddressData;
  headerData: MasterDataPdfHeaderData;
  footerData: MasterDataPdfFooterData;
  infoblockData: MasterDataPdfInfoblockData;
  linkToAgb: string;
}

/**
 * MasterDataPdfActive is the assignment of the master data a
 * company's documents are rendered with. A null defaultId means the
 * company has none assigned.
 */
export interface MasterDataPdfActive {
  companyId: string;
  defaultId: string | null;
}

export type MasterDataPdfOrderBy = "label" | "id";

export interface ListMasterDataPdfRequest {
  company?: Filter<string>;
  orderBy?: OrdrBy<MasterDataPdfOrderBy>[];
  limit?: number;
  offset?: number;
}

export type MasterDataPdfList = Lst<MasterDataPdfDefault>;

/**
 * The maximum number of lines the info block has room for. The
 * backend rejects more, and consumers may add a line of their own
 * when they render - the document date, typically.
 */
export const maxInfoblockLines = 15;

/** The maximum length of a sender line in the address window. */
export const maxSenderLineChars = 70;

export function infoblockLineCount(data: MasterDataPdfInfoblockData): number {
  return data.groups.reduce((count, group) => count + group.lines.length, 0);
}

// The info block groups and lines are plain positional lists -
// they carry neither an id nor an order field - so the helpers
// below address them by index rather than by id.

export function addInfoblockGroup(
  data: MasterDataPdfInfoblockData,
): MasterDataPdfInfoblockData {
  return { groups: [...data.groups, { lines: [] }] };
}

export function removeInfoblockGroup(
  data: MasterDataPdfInfoblockData,
  groupIndex: number,
): MasterDataPdfInfoblockData {
  return { groups: data.groups.filter((_, i) => i !== groupIndex) };
}

export function moveInfoblockGroup(
  data: MasterDataPdfInfoblockData,
  groupIndex: number,
  by: number,
): MasterDataPdfInfoblockData {
  const to = groupIndex + by;
  if (to < 0 || to >= data.groups.length) {
    return data;
  }
  const groups = [...data.groups];
  [groups[groupIndex], groups[to]] = [groups[to], groups[groupIndex]];
  return { groups };
}

/** setInfoblockLines replaces the lines of one group. */
export function setInfoblockLines(
  data: MasterDataPdfInfoblockData,
  groupIndex: number,
  lines: MasterDataPdfInfoblockLine[],
): MasterDataPdfInfoblockData {
  return mapGroup(data, groupIndex, () => ({ lines }));
}

function mapGroup(
  data: MasterDataPdfInfoblockData,
  groupIndex: number,
  map: (group: MasterDataPdfInfoblockGroup) => MasterDataPdfInfoblockGroup,
): MasterDataPdfInfoblockData {
  return {
    groups: data.groups.map((group, i) =>
      i === groupIndex ? map(group) : group,
    ),
  };
}
